import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Send, Users, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MESSAGE_TEMPLATES = [
  {
    id: 'greeting',
    name: 'Friendly Greeting',
    message: "Hi {{name}}! Thanks for showing interest. I'm reaching out from AILeadMantra to help you with your requirements. When would be a good time to connect?"
  },
  {
    id: 'followup',
    name: 'Follow-up',
    message: "Hi {{name}}! Just following up on our previous conversation. Do you have any questions I can help with? Looking forward to hearing from you!"
  },
  {
    id: 'offer',
    name: 'Special Offer',
    message: "Hi {{name}}! We have an exclusive offer for you. Get 20% off on our premium services this week only. Interested? Let's chat!"
  },
  {
    id: 'meeting',
    name: 'Schedule Meeting',
    message: "Hi {{name}}! I'd love to schedule a quick call to discuss how we can help you. Are you available this week for a 15-minute chat?"
  },
  {
    id: 'custom',
    name: 'Custom Message',
    message: ''
  }
];

const BulkWhatsAppModal = ({ isOpen, onClose, selectedLeads }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('greeting');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  if (!isOpen) return null;

  const leadsWithPhone = selectedLeads.filter(lead => lead.phone);
  const leadsWithoutPhone = selectedLeads.filter(lead => !lead.phone);

  const getMessageContent = () => {
    if (selectedTemplate === 'custom') {
      return customMessage;
    }
    const template = MESSAGE_TEMPLATES.find(t => t.id === selectedTemplate);
    return template?.message || '';
  };

  const formatPhoneForWhatsApp = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // Handle Indian numbers
    if (cleaned.length === 10) {
      cleaned = '91' + cleaned;
    } else if (cleaned.startsWith('0') && cleaned.length === 11) {
      cleaned = '91' + cleaned.slice(1);
    } else if (cleaned.startsWith('+')) {
      cleaned = cleaned.slice(1);
    }
    
    return cleaned;
  };

  const sendBulkMessages = async () => {
    if (leadsWithPhone.length === 0) {
      toast.error('No leads with phone numbers selected');
      return;
    }

    setSending(true);
    const messageTemplate = getMessageContent();
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Use bulk-send API endpoint
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/bulk-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_ids: leadsWithPhone.map(l => l.id),
          template_id: selectedTemplate === 'custom' ? null : selectedTemplate,
          variables: {
            company: 'AILeadMantra'
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Sending messages to ${data.total_leads} leads...`);
        
        // Poll for status
        const checkStatus = async () => {
          const statusRes = await fetch(`${BACKEND_URL}/api/whatsapp/bulk-status/${data.job_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (statusRes.ok) {
            const status = await statusRes.json();
            if (status.status === 'completed') {
              setResults({
                successful: status.sent,
                failed: status.failed,
                total: status.total
              });
              setSending(false);
              toast.success(`Sent: ${status.sent}, Failed: ${status.failed}`);
            } else {
              // Check again in 2 seconds
              setTimeout(checkStatus, 2000);
            }
          }
        };
        
        setTimeout(checkStatus, 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send messages');
        setSending(false);
      }
    } catch (error) {
      console.error('Bulk send error:', error);
      toast.error('Failed to send bulk messages');
      setSending(false);
    }
  };

  const openWhatsAppLinks = async () => {
    if (leadsWithPhone.length === 0) {
      toast.error('No leads with phone numbers selected');
      return;
    }

    setSending(true);
    const messageTemplate = getMessageContent();
    const successfulLeads = [];
    const failedLeads = [];

    // Log bulk action
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${BACKEND_URL}/api/leads/bulk-whatsapp-log`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_ids: leadsWithPhone.map(l => l.id),
          template_id: selectedTemplate,
          message_preview: messageTemplate.slice(0, 100)
        })
      });
    } catch (error) {
      console.error('Failed to log bulk action:', error);
    }

    // Open WhatsApp links with slight delay to avoid popup blocking
    for (let i = 0; i < leadsWithPhone.length; i++) {
      const lead = leadsWithPhone[i];
      try {
        const phone = formatPhoneForWhatsApp(lead.phone);
        const personalizedMessage = messageTemplate.replace(/\{\{name\}\}/g, lead.name || 'there');
        const encodedMessage = encodeURIComponent(personalizedMessage);
        const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
        
        // Open in new tab
        window.open(whatsappUrl, '_blank');
        successfulLeads.push(lead);
        
        // Small delay between opens to avoid browser blocking
        if (i < leadsWithPhone.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        failedLeads.push(lead);
      }
    }

    setResults({
      successful: successfulLeads.length,
      failed: failedLeads.length,
      total: leadsWithPhone.length
    });

    setSending(false);

    if (successfulLeads.length > 0) {
      toast.success(`Opened ${successfulLeads.length} WhatsApp chats`);
    }
  };

  const resetAndClose = () => {
    setSelectedTemplate('greeting');
    setCustomMessage('');
    setResults(null);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={resetAndClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-br from-emerald-900 to-gray-900 rounded-2xl w-full max-w-lg border border-emerald-700/30 shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-emerald-700/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Bulk WhatsApp</h3>
                  <p className="text-emerald-300/70 text-sm">
                    Send to {leadsWithPhone.length} lead{leadsWithPhone.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={resetAndClose}>
                <X className="w-5 h-5 text-gray-400" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {results ? (
              /* Results View */
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Messages Initiated!</h4>
                <p className="text-emerald-300/70 mb-4">
                  Opened {results.successful} WhatsApp chat{results.successful !== 1 ? 's' : ''}
                </p>
                {results.failed > 0 && (
                  <p className="text-red-400 text-sm mb-4">
                    {results.failed} failed to open
                  </p>
                )}
                <Button onClick={resetAndClose} className="bg-emerald-600 hover:bg-emerald-500">
                  Done
                </Button>
              </div>
            ) : (
              <>
                {/* Warnings */}
                {leadsWithoutPhone.length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div className="text-sm">
                      <span className="text-yellow-300 font-medium">{leadsWithoutPhone.length} lead{leadsWithoutPhone.length !== 1 ? 's' : ''}</span>
                      <span className="text-yellow-300/70"> without phone number will be skipped</span>
                    </div>
                  </div>
                )}

                {/* Selected Leads Preview */}
                <div className="bg-emerald-950/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-300">Selected Leads</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {leadsWithPhone.slice(0, 5).map(lead => (
                      <span key={lead.id} className="px-2 py-1 bg-emerald-800/50 rounded text-xs text-white">
                        {lead.name}
                      </span>
                    ))}
                    {leadsWithPhone.length > 5 && (
                      <span className="px-2 py-1 bg-emerald-800/50 rounded text-xs text-emerald-300">
                        +{leadsWithPhone.length - 5} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    Message Template
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {MESSAGE_TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        className={`p-3 rounded-lg text-left text-sm transition-all ${
                          selectedTemplate === template.id
                            ? 'bg-green-500/20 border-2 border-green-500 text-white'
                            : 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-300 hover:border-emerald-500'
                        }`}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message Preview / Custom Input */}
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    {selectedTemplate === 'custom' ? 'Custom Message' : 'Message Preview'}
                  </label>
                  {selectedTemplate === 'custom' ? (
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Type your message here... Use {{name}} for personalization"
                      className="w-full h-32 px-4 py-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg text-white placeholder-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  ) : (
                    <div className="p-4 bg-emerald-950/50 rounded-lg border border-emerald-700/30">
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">
                        {getMessageContent().replace(/\{\{name\}\}/g, leadsWithPhone[0]?.name || 'Lead Name')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    onClick={sendBulkMessages}
                    disabled={sending || leadsWithPhone.length === 0 || (selectedTemplate === 'custom' && !customMessage)}
                    className="w-full bg-green-600 hover:bg-green-500 text-white"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending Messages...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send WhatsApp Messages ({leadsWithPhone.length})
                      </>
                    )}
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetAndClose}
                      className="flex-1 border-gray-600 text-gray-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={openWhatsAppLinks}
                      variant="outline"
                      disabled={sending || leadsWithPhone.length === 0 || (selectedTemplate === 'custom' && !customMessage)}
                      className="flex-1 border-green-700/50 text-green-300"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open wa.me Links
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkWhatsAppModal;
